import React, { useEffect, useState, useRef } from 'react';
import { SendIcon, SmartphoneIcon, GlobeIcon, WifiIcon, DollarSignIcon, CheckCircleIcon } from 'lucide-react';
import { Button } from './Button';
type Language = 'fa' | 'ar' | 'en' | 'ur';
type Message = {
  id: string;
  type: 'user' | 'ai';
  content: string;
  options?: {
    type: 'countries' | 'duration' | 'usage' | 'providers' | 'comparison' | 'final_offer' | 'buttons';
    items: Array<{
      id: string;
      text: string;
      action?: string;
    }>;
  };
};
const translations = {
  fa: {
    dir: 'rtl',
    font: 'Vazirmatn',
    header: {
      title: 'دستیار سفر شما',
      subtitle: 'بهترین گزینه رو برات پیدا می‌کنم'
    },
    greeting: 'سلام! 👋 کجا می‌خوای سفر بری؟',
    placeholder: 'پیام خود را بنویسید...',
    countries: [{
      id: 'turkey',
      text: 'ترکیه'
    }, {
      id: 'uae',
      text: 'امارات'
    }, {
      id: 'iraq',
      text: 'عراق'
    }, {
      id: 'georgia',
      text: 'گرجستان'
    }, {
      id: 'europe',
      text: 'اروپا'
    }, {
      id: 'usa',
      text: 'آمریکا'
    }, {
      id: 'other',
      text: 'کشور دیگه'
    }],
    durationQuestion: 'چند روز می‌خوای در {country} بمونی؟',
    durations: [{
      id: '1-3',
      text: '۱ تا ۳ روز'
    }, {
      id: '4-7',
      text: '۴ تا ۷ روز'
    }, {
      id: '8-14',
      text: '۸ تا ۱۴ روز'
    }, {
      id: '15-30',
      text: '۱۵ تا ۳۰ روز'
    }, {
      id: '30+',
      text: 'بیشتر از ۳۰ روز'
    }],
    usageQuestion: 'چقدر فکر می‌کنی از اینترنت استفاده کنی؟',
    usageOptions: [{
      id: 'light',
      text: 'کم - فقط پیام و مپ'
    }, {
      id: 'medium',
      text: 'متوسط - شبکه‌های اجتماعی'
    }, {
      id: 'heavy',
      text: 'زیاد - ویدیو و استریم'
    }, {
      id: 'unlimited',
      text: 'خیلی زیاد - کار آنلاین'
    }],
    providerQuestion: 'الان از کدوم اپراتور استفاده می‌کنی؟',
    providers: [{
      id: 'irancell',
      text: 'ایرانسل'
    }, {
      id: 'mci',
      text: 'همراه اول'
    }, {
      id: 'rightel',
      text: 'رایتل'
    }, {
      id: 'other',
      text: 'اپراتور دیگه'
    }]
  },
  en: {
    dir: 'ltr',
    font: 'Inter',
    header: {
      title: 'Your Travel Assistant',
      subtitle: "I'll find the best option for you"
    },
    greeting: 'Hi! 👋 Where would you like to travel?',
    placeholder: 'Type your message...',
    countries: [{
      id: 'turkey',
      text: 'Turkey'
    }, {
      id: 'uae',
      text: 'UAE'
    }, {
      id: 'iraq',
      text: 'Iraq'
    }, {
      id: 'georgia',
      text: 'Georgia'
    }, {
      id: 'europe',
      text: 'Europe'
    }, {
      id: 'usa',
      text: 'USA'
    }, {
      id: 'other',
      text: 'Other Country'
    }],
    durationQuestion: 'How many days will you stay in {country}?',
    durations: [{
      id: '1-3',
      text: '1-3 days'
    }, {
      id: '4-7',
      text: '4-7 days'
    }, {
      id: '8-14',
      text: '1-2 weeks'
    }, {
      id: '15-30',
      text: '2-4 weeks'
    }, {
      id: '30+',
      text: 'More than a month'
    }],
    usageQuestion: "How much do you think you'll use the internet?",
    usageOptions: [{
      id: 'light',
      text: 'Light - Just messaging & maps'
    }, {
      id: 'medium',
      text: 'Medium - Social media'
    }, {
      id: 'heavy',
      text: 'Heavy - Video & streaming'
    }, {
      id: 'unlimited',
      text: 'Very Heavy - Online work'
    }],
    providerQuestion: 'Which mobile provider do you currently use?',
    providers: [{
      id: 'verizon',
      text: 'Verizon'
    }, {
      id: 'att',
      text: 'AT&T'
    }, {
      id: 'tmobile',
      text: 'T-Mobile'
    }, {
      id: 'other',
      text: 'Other Provider'
    }]
  },
  ar: {
    dir: 'rtl',
    font: 'IBM Plex Sans Arabic',
    header: {
      title: 'مساعد السفر الخاص بك',
      subtitle: 'سأجد لك أفضل خيار'
    },
    greeting: 'مرحباً! 👋 إلى أين تريد السفر؟',
    placeholder: 'اكتب رسالتك...',
    countries: [{
      id: 'turkey',
      text: 'تركيا'
    }, {
      id: 'uae',
      text: 'الإمارات'
    }, {
      id: 'iraq',
      text: 'العراق'
    }, {
      id: 'georgia',
      text: 'جورجيا'
    }, {
      id: 'europe',
      text: 'أوروبا'
    }, {
      id: 'usa',
      text: 'أمريكا'
    }, {
      id: 'other',
      text: 'دولة أخرى'
    }],
    durationQuestion: 'كم يوماً ستبقى في {country}؟',
    durations: [{
      id: '1-3',
      text: '١-٣ أيام'
    }, {
      id: '4-7',
      text: '٤-٧ أيام'
    }, {
      id: '8-14',
      text: '١-٢ أسبوع'
    }, {
      id: '15-30',
      text: '٢-٤ أسابيع'
    }, {
      id: '30+',
      text: 'أكثر من شهر'
    }],
    usageQuestion: 'كم تعتقد أنك ستستخدم الإنترنت؟',
    usageOptions: [{
      id: 'light',
      text: 'قليل - صرف پیغام اور نقشے'
    }, {
      id: 'medium',
      text: 'درمیانہ - سوشل میڈیا'
    }, {
      id: 'heavy',
      text: 'كثير - فيديو اور سٹریمنگ'
    }, {
      id: 'unlimited',
      text: 'كثير جداً - عمل أونلاين'
    }],
    providerQuestion: 'أي مزود خدمة تستخدم حالياً؟',
    providers: [{
      id: 'etisalat',
      text: 'اتصالات'
    }, {
      id: 'du',
      text: 'دو'
    }, {
      id: 'stc',
      text: 'STC'
    }, {
      id: 'other',
      text: 'مزود آخر'
    }]
  },
  ur: {
    dir: 'rtl',
    font: 'Noto Nastaliq Urdu',
    header: {
      title: 'آپ کا سفری معاون',
      subtitle: 'میں آپ کے لیے بہترین آپشن تلاش کروں گا'
    },
    greeting: 'السلام علیکم! 👋 آپ کہاں سفر کرنا چاہتے ہیں؟',
    placeholder: 'اپنا پیغام لکھیں...',
    countries: [{
      id: 'turkey',
      text: 'ترکی'
    }, {
      id: 'uae',
      text: 'متحدہ عرب امارات'
    }, {
      id: 'iraq',
      text: 'عراق'
    }, {
      id: 'georgia',
      text: 'جارجیا'
    }, {
      id: 'europe',
      text: 'یورپ'
    }, {
      id: 'usa',
      text: 'امریکہ'
    }, {
      id: 'other',
      text: 'دوسرا ملک'
    }],
    durationQuestion: 'آپ {country} میں کتنے دن رہیں گے؟',
    durations: [{
      id: '1-3',
      text: '۱-۳ دن'
    }, {
      id: '4-7',
      text: '۴-۷ دن'
    }, {
      id: '8-14',
      text: '۱-۲ ہفتے'
    }, {
      id: '15-30',
      text: '۲-۴ ہفتے'
    }, {
      id: '30+',
      text: 'ایک مہینے سے زیادہ'
    }],
    usageQuestion: 'آپ کتنا انٹرنیٹ استعمال کریں گے؟',
    usageOptions: [{
      id: 'light',
      text: 'کم - صرف پیغام اور نقشے'
    }, {
      id: 'medium',
      text: 'درمیانہ - سوشل میڈیا'
    }, {
      id: 'heavy',
      text: 'زیادہ - ویڈیو اور سٹریمنگ'
    }, {
      id: 'unlimited',
      text: 'بہت زیادہ - آن لائن کام'
    }],
    providerQuestion: 'آپ فی الوقت کون سا موبائل آپریٹر استعمال کرتے ہیں؟',
    providers: [{
      id: 'jazz',
      text: 'جاز'
    }, {
      id: 'telenor',
      text: 'ٹیلی نور'
    }, {
      id: 'zong',
      text: 'زونگ'
    }, {
      id: 'other',
      text: 'دوسرا آپریٹر'
    }]
  }
};
// Pricing data for different countries and usage patterns
const pricingData = {
  turkey: {
    light: {
      data: '2GB',
      price: 8.99,
      roaming: {
        irancell: 15,
        mci: 12,
        rightel: 10
      }
    },
    medium: {
      data: '5GB',
      price: 14.99,
      roaming: {
        irancell: 35,
        mci: 30,
        rightel: 25
      }
    },
    heavy: {
      data: '10GB',
      price: 24.99,
      roaming: {
        irancell: 70,
        mci: 60,
        rightel: 50
      }
    },
    unlimited: {
      data: '20GB',
      price: 39.99,
      roaming: {
        irancell: 140,
        mci: 120,
        rightel: 100
      }
    }
  },
  uae: {
    light: {
      data: '2GB',
      price: 12.99,
      roaming: {
        irancell: 18,
        mci: 15,
        rightel: 12
      }
    },
    medium: {
      data: '5GB',
      price: 19.99,
      roaming: {
        irancell: 45,
        mci: 37,
        rightel: 30
      }
    },
    heavy: {
      data: '10GB',
      price: 32.99,
      roaming: {
        irancell: 90,
        mci: 75,
        rightel: 60
      }
    },
    unlimited: {
      data: '20GB',
      price: 54.99,
      roaming: {
        irancell: 180,
        mci: 150,
        rightel: 120
      }
    }
  },
  europe: {
    light: {
      data: '3GB',
      price: 15.99,
      roaming: {
        irancell: 25,
        mci: 22,
        rightel: 18
      }
    },
    medium: {
      data: '8GB',
      price: 28.99,
      roaming: {
        irancell: 60,
        mci: 52,
        rightel: 45
      }
    },
    heavy: {
      data: '15GB',
      price: 45.99,
      roaming: {
        irancell: 120,
        mci: 105,
        rightel: 90
      }
    },
    unlimited: {
      data: '25GB',
      price: 69.99,
      roaming: {
        irancell: 200,
        mci: 175,
        rightel: 150
      }
    }
  }
};
export function AIChat() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [language, setLanguage] = useState<Language>('en');
  const [userSelection, setUserSelection] = useState({
    country: '',
    duration: '',
    usage: '',
    provider: ''
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const t = translations[language];
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  useEffect(() => {
    setMessages([{
      id: '1',
      type: 'ai',
      content: t.greeting,
      options: {
        type: 'countries',
        items: t.countries
      }
    }]);
  }, [language]);
  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang);
  };
  const handleOptionSelect = async (optionId: string, type: string) => {
    const selectedOption = t[type as keyof typeof t]?.find?.((item: any) => item.id === optionId) || {
      id: optionId,
      text: optionId
    };
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: selectedOption.text
    };
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    let aiResponse: Message;
    switch (type) {
      case 'countries':
        setUserSelection(prev => ({
          ...prev,
          country: optionId
        }));
        const countryName = selectedOption.text;
        aiResponse = {
          id: Date.now().toString(),
          type: 'ai',
          content: t.durationQuestion.replace('{country}', countryName),
          options: {
            type: 'duration',
            items: t.durations
          }
        };
        break;
      case 'duration':
        setUserSelection(prev => ({
          ...prev,
          duration: optionId
        }));
        aiResponse = {
          id: Date.now().toString(),
          type: 'ai',
          content: t.usageQuestion,
          options: {
            type: 'usage',
            items: t.usageOptions
          }
        };
        break;
      case 'usage':
        setUserSelection(prev => ({
          ...prev,
          usage: optionId
        }));
        aiResponse = {
          id: Date.now().toString(),
          type: 'ai',
          content: t.providerQuestion,
          options: {
            type: 'providers',
            items: t.providers
          }
        };
        break;
      case 'providers':
        setUserSelection(prev => ({
          ...prev,
          provider: optionId
        }));
        // Generate comparison and final offer
        const country = userSelection.country;
        const usage = userSelection.usage;
        const provider = optionId;
        const esimData = pricingData[country]?.[usage] || pricingData.turkey.medium;
        const roamingCost = esimData.roaming[provider] || 50;
        const savings = roamingCost - esimData.price;
        const savingsPercent = Math.round(savings / roamingCost * 100);
        let comparisonContent = '';
        if (language === 'fa') {
          comparisonContent = `🔍 مقایسه قیمت‌ها:
📱 رومینگ ${selectedOption.text}:
• هزینه: $${roamingCost}
• سرعت محدود
• ممکنه قطع بشه
🌐 eSIM ما:
• ${esimData.data} دیتا: $${esimData.price}
• سرعت 4G/5G
• پوشش عالی
💰 صرفه‌جویی شما: $${savings} (${savingsPercent}%)
می‌خوای این پلن رو خریداری کنی؟`;
        } else {
          comparisonContent = `🔍 Price Comparison:
📱 ${selectedOption.text} Roaming:
• Cost: $${roamingCost}
• Limited speed
• May disconnect
🌐 Our eSIM:
• ${esimData.data} data: $${esimData.price}
• 4G/5G speed
• Excellent coverage
💰 Your savings: $${savings} (${savingsPercent}%)
Would you like to purchase this plan?`;
        }
        aiResponse = {
          id: Date.now().toString(),
          type: 'ai',
          content: comparisonContent,
          options: {
            type: 'final_offer',
            items: [{
              id: 'buy_now',
              text: language === 'fa' ? 'بله، خرید می‌کنم' : "Yes, I'll buy it",
              action: 'primary'
            }, {
              id: 'more_info',
              text: language === 'fa' ? 'اطلاعات بیشتر' : 'More information'
            }, {
              id: 'start_over',
              text: language === 'fa' ? 'شروع مجدد' : 'Start over'
            }]
          }
        };
        break;
      case 'final_offer':
        if (optionId === 'buy_now') {
          aiResponse = {
            id: Date.now().toString(),
            type: 'ai',
            content: language === 'fa' ? '🎉 عالی! برای خرید روی دکمه "Get Your eSIM" کلیک کن تا به صفحه خرید منتقل بشی.' : '🎉 Great! Click the "Get Your eSIM" button to proceed to purchase.',
            options: {
              type: 'buttons',
              items: [{
                id: 'new_search',
                text: language === 'fa' ? 'جستجوی جدید' : 'New search'
              }]
            }
          };
        } else if (optionId === 'start_over') {
          setUserSelection({
            country: '',
            duration: '',
            usage: '',
            provider: ''
          });
          aiResponse = {
            id: Date.now().toString(),
            type: 'ai',
            content: t.greeting,
            options: {
              type: 'countries',
              items: t.countries
            }
          };
        } else {
          aiResponse = {
            id: Date.now().toString(),
            type: 'ai',
            content: language === 'fa' ? '📋 eSIM ما شامل:\n• نصب آسان با QR کد\n• فعال‌سازی فوری\n• پشتیبانی ۲۴/۷\n• بدون قرارداد\n• کیفیت تضمینی' : '📋 Our eSIM includes:\n• Easy installation with QR code\n• Instant activation\n• 24/7 support\n• No contract\n• Quality guaranteed',
            options: {
              type: 'buttons',
              items: [{
                id: 'buy_now',
                text: language === 'fa' ? 'خرید می‌کنم' : "I'll buy it",
                action: 'primary'
              }, {
                id: 'start_over',
                text: language === 'fa' ? 'شروع مجدد' : 'Start over'
              }]
            }
          };
        }
        break;
      default:
        aiResponse = {
          id: Date.now().toString(),
          type: 'ai',
          content: language === 'fa' ? 'چطور می‌تونم کمکتون کنم؟' : 'How can I help you?',
          options: {
            type: 'buttons',
            items: [{
              id: 'new_search',
              text: language === 'fa' ? 'جستجوی جدید' : 'New search'
            }]
          }
        };
    }
    setMessages(prev => [...prev, aiResponse]);
    setIsTyping(false);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: inputValue
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    const aiResponse: Message = {
      id: Date.now().toString(),
      type: 'ai',
      content: language === 'fa' ? 'لطفاً از گزینه‌های بالا انتخاب کنید تا بتونم بهترین پیشنهاد رو بدم.' : 'Please select from the options above so I can give you the best recommendation.',
      options: {
        type: 'countries',
        items: t.countries
      }
    };
    setMessages(prev => [...prev, aiResponse]);
    setIsTyping(false);
  };
  return <div className={`w-full max-w-[360px] bg-white rounded-2xl shadow-md`} dir={t.dir} data-id="element-73">
      {/* Header */}
      <div className="p-6 border-b border-gray-100" data-id="element-74">
        <div className="flex items-center justify-between" data-id="element-75">
          <div className="flex items-center gap-3" data-id="element-76">
            <div className="w-7 h-7 bg-[#2563EB] rounded-full flex items-center justify-center" data-id="element-77">
              <SmartphoneIcon className="w-4 h-4 text-white" data-id="element-78" />
            </div>
            <div data-id="element-79">
              <h3 className={`font-bold text-lg font-[${t.font}]`} data-id="element-80">
                {t.header.title}
              </h3>
              <p className={`text-sm text-gray-500 font-[${t.font}]`} data-id="element-81">
                {t.header.subtitle}
              </p>
            </div>
          </div>
          <div className="relative" data-id="element-82">
            <select value={language} onChange={e => handleLanguageChange(e.target.value as Language)} className="appearance-none bg-transparent border border-gray-200 rounded-lg px-2 py-1 pr-8" data-id="element-83">
              <option value="en" data-id="element-84">🇬🇧</option>
              <option value="ar" data-id="element-85">🇦🇪</option>
              <option value="fa" data-id="element-86">🇮🇷</option>
              <option value="ur" data-id="element-87">🇵🇰</option>
            </select>
            <GlobeIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-88" />
          </div>
        </div>
      </div>
      {/* Messages */}
      <div ref={messagesContainerRef} className="h-[400px] overflow-y-auto p-6 space-y-4 scroll-smooth" data-id="element-89">
        {messages.map(message => <div key={message.id} className={`flex ${message.type === 'user' ? t.dir === 'rtl' ? 'justify-start' : 'justify-end' : t.dir === 'rtl' ? 'justify-end' : 'justify-start'}`} data-id="element-90">
            <div className={`max-w-[80%] rounded-xl p-3 ${message.type === 'user' ? 'bg-[#DBEAFE] text-gray-800 rounded-bl-none' : 'bg-[#F3F4F6] text-gray-800 rounded-br-none'}`} data-id="element-91">
              <p className={`text-[14px] font-[${t.font}] whitespace-pre-line`} data-id="element-92">
                {message.content}
              </p>
              {message.options && <div className="mt-3 space-y-2" data-id="element-93">
                  {message.options.items.map(item => <button key={item.id} onClick={() => handleOptionSelect(item.id, message.options?.type || '')} className={`w-full text-${t.dir === 'rtl' ? 'right' : 'left'} px-4 py-3 text-sm rounded-full transition-colors font-[${t.font}] ${item.action === 'primary' ? 'bg-[#F97316] text-white hover:bg-[#EA580C]' : 'border border-[#2563EB] text-[#2563EB] hover:bg-blue-50'}`} data-id="element-94">
                      {item.text}
                    </button>)}
                </div>}
            </div>
          </div>)}
        {isTyping && <div className={`flex ${t.dir === 'rtl' ? 'justify-start' : 'justify-end'} items-center space-x-2`} data-id="element-95">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-0" data-id="element-96"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-150" data-id="element-97"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-300" data-id="element-98"></div>
          </div>}
        <div ref={messagesEndRef} data-id="element-99" />
      </div>
      {/* Input */}
      <div className="p-6 border-t border-gray-100" data-id="element-100">
        <form onSubmit={handleSubmit} className="flex items-center gap-2" data-id="element-101">
          {t.dir === 'rtl' ? <>
              <Button type="submit" variant="primary" size="sm" data-id="element-102">
                <SendIcon className="w-4 h-4" data-id="element-103" />
              </Button>
              <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={t.placeholder} className={`flex-1 px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB] font-[${t.font}] text-right`} data-id="element-104" />
            </> : <>
              <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={t.placeholder} className={`flex-1 px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB] font-[${t.font}] text-left`} data-id="element-105" />
              <Button type="submit" variant="primary" size="sm" data-id="element-106">
                <SendIcon className="w-4 h-4" data-id="element-107" />
              </Button>
            </>}
        </form>
      </div>
    </div>;
}